import { Button, IncompletePaymentData, User, onUserChange } from '@hub/shared'
import { RecruitmentScope } from '@hub/shared/interfaces/user-in-campaign.interface'
import { SqCallout, SqLoader, SqModal } from '@squidit/react-css'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { SubscriptionLike } from 'rxjs'
import { CampaignObject } from '../campaign-footer/campaign-footer.component'
import CnpjAdvantages from './componentes/cnpj-advantages/cnpj-advantages.component'
import EditAddress from './componentes/edit-address/edit-address.component'
import './modal-contract-accepted.component.scoped.scss'
import './modal-contract-accepted.component.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  openModal: boolean
  campaignId: string
  idNetwork: string
  opportunity: boolean
  userScope: RecruitmentScope
  campaignObject: CampaignObject
  onCloseModal?: (close: boolean) => any
}

export default ({ openModal, campaignId, idNetwork, opportunity, userScope, onCloseModal, campaignObject }: Props) => {
  const [t] = useTranslation('modalContractAccepted')
  const [width, setWidth] = useState<number>(window?.innerWidth)
  const [height, setHeight] = useState<number>(window?.innerHeight)
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(true)
  const [user, setUser] = useState<User | null>(null)
  const navigate = useNavigate()
  const [internationalPayment, setInternationalPayment] = useState<boolean>(false)
  const [hasKit, setHasKit] = useState<boolean>(false)
  const [hasPayment, setHasPayment] = useState<boolean>(false)

  const campaignPayments = useMemo(() => campaignObject?.campaign?.activation?.payments, [campaignObject?.campaign?.activation?.payments])

  const hasCacheData = useMemo(() => {
    return user?.[campaignObject?.campaign?.socialNetworks?.[0]]
      ?.find((profile) => profile?.id === idNetwork)
      ?.socialNetworkProfilesCache?.every((field) => field?.contentValue)
  }, [campaignObject?.campaign?.socialNetworks, user, idNetwork])

  const isShowPaymentBox = useMemo(() => hasPayment && !internationalPayment, [hasPayment, internationalPayment])
  const isShowIncompletePaymentData = useMemo(() => isShowPaymentBox && !user?.banks?.[0]?.id, [user?.banks, isShowPaymentBox])
  const isShowCnpjAdvantages = useMemo(
    () =>
      (isShowPaymentBox && user?.banks?.[0]?.paymentType === 'rpa') ||
      (user?.profileadditionalinfo?.country === 'BR' && !user?.banks?.[0]?.id),
    [user?.banks, user?.profileadditionalinfo?.country, isShowPaymentBox]
  )
  const isShowUpdateValuesCache = useMemo(
    () => !isShowIncompletePaymentData && !isShowCnpjAdvantages && !hasCacheData,
    [hasCacheData, isShowCnpjAdvantages, isShowIncompletePaymentData]
  )

  useLayoutEffect(() => {
    const updateWidth = () => {
      setWidth(window?.innerWidth)
    }
    const updateHeight = () => {
      setHeight(window?.innerHeight)
    }
    window.addEventListener('resize', updateWidth)
    window.addEventListener('resize', updateHeight)
    return () => {
      window.removeEventListener('resize', updateWidth)
      window.removeEventListener('resize', updateHeight)
    }
  }, [])

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe(async (userChanged: User) => {
      setLoading(true)
      if (userChanged?.squidId) {
        setUser(userChanged)
        const documentTypes = ['CPF', 'CNPJ']
        if (documentTypes?.findIndex((document) => document === userChanged?.profileadditionalinfo?.documentType) === -1) {
          setInternationalPayment(true)
        } else {
          setInternationalPayment(false)
        }
      }
      setLoading(false)
    })
    return () => {
      onUserChangeSubscription.current?.unsubscribe()
    }
  }, [onUserChangeSubscription])

  useEffect(() => {
    if (userScope?.payments?.length) {
      const kit = userScope?.payments?.find((item) => item.paymentType === 'product')
      setHasKit(!!kit)
      const payment = userScope?.payments?.find((item) => item.paymentType === 'cash')
      setHasPayment(!!payment)
    }
  }, [userScope, campaignPayments])

  const redirect = (path: string) => {
    navigate(path)
  }

  const handleCloseModal = (value: boolean) => {
    if (onCloseModal && typeof onCloseModal === 'function') {
      onCloseModal(value)
    }
  }

  let footer = (
    <div className="display-flex justify-content-space-between" style={{ width: '100%' }}>
      <Button
        id="modal-contract-accepted-cancel"
        onClick={() => handleCloseModal(true)}
        size="sm"
        color="transparent"
        textColor="var(--gray)"
        borderColor="var(--gray)"
      >
        <span>{t('cancel')}</span>
      </Button>
      <Button
        id="modal-contract-accepted-confirm"
        onClick={() => (opportunity ? handleCloseModal(true) : redirect(`/campaigns/${campaignId}/details/${idNetwork}`))}
        size="sm"
      >
        <span>{opportunity ? t('confirm') : t('goToCampaign')}</span>
      </Button>
    </div>
  )

  let updateValuesCache = (
    <SqCallout icon="fa-solid fa-diamond-exclamation" title={t('cache.title')} smallIconStyle backgroundColor="var(--background)">
      <div className="display-flex-column">
        <p dangerouslySetInnerHTML={{ __html: t('cache.description') }} />
        <Button
          className="mt-3"
          width="100%"
          color="var(--background_secondary)"
          textColor="var(--primary_color)"
          borderColor="var(--primary_color)"
          onClick={() => navigate('/profile/social-networks')}
        >
          {t('cache.buttonLabel')}
        </Button>
      </div>
    </SqCallout>
  )

  let content = (
    <div
      className="content scrollbar"
      style={width < 991 ? { height: `calc(${height}px - 58px - 61px)` } : { maxHeight: `calc(${height}px - 58px - 61px)` }}
    >
      {loading ? (
        <div style={{ margin: 'auto' }}>
          <SqLoader size="big" />
        </div>
      ) : (
        <div>
          <section style={{ textAlign: 'center' }}>
            <i
              className={`${opportunity ? 'fa-light fa-thumbs-up' : 'fa-light fa-badge-check'}`}
              style={{ color: 'var(--green)', fontSize: '12rem' }}
            ></i>
          </section>
          <section className="text">
            <h1>{opportunity ? t('interestSent') : t('congratulations')}</h1>
            <p>{opportunity ? t('messageOpportunity') : t('message')}</p>
          </section>
          {hasKit ? <EditAddress user={user} opportunity={opportunity} onRedirect={(path) => redirect(path)} /> : null}
          {isShowPaymentBox ? (
            <div>
              {isShowIncompletePaymentData ? (
                <section>
                  <hr />
                  <IncompletePaymentData redirect={(path) => redirect(path)} />
                </section>
              ) : null}
              {isShowCnpjAdvantages ? <CnpjAdvantages /> : null}
            </div>
          ) : null}
          {isShowUpdateValuesCache ? updateValuesCache : null}
        </div>
      )}
    </div>
  )

  return (
    <SqModal
      className="modal-contract-accepted"
      open={openModal}
      onCloseChange={() => handleCloseModal(true)}
      modalSize={width < 991 ? 'lg' : 'md'}
      footer={footer}
    >
      {content}
    </SqModal>
  )
}
